// React Component
import React, { useMemo, useRef, useState } from "react"
import { NavLink, useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"


//App Component
import { Routes } from "../routes"
import { Images } from "../constants"
import { FamlaServices } from "../services"
import { formatNumber } from "../utils/formatDate"
import { planLabelFromCompany } from "../utils/planLabelFromCompany"
import { logout, useAppSelector } from "../stores"
import { Dropdown, SearchInput } from "../components"
import { i18n } from '../services'

//interface
import { iAlert } from "../interfaces"
import { ApprovalRequestModal, ChangeCompanyModal, InviteOwnerModal } from "../views/modal"
import { switchActiveCompany } from "../utils/switchActiveCompany"
import { iCompany, iUser } from "../components/type"
import { useCountdown, openPDFInViewer } from "../utils"
import CreditsService from "../services/api/creditsService"
import { isPrivateEmail } from "../utils/emailUtils"
import { getProcessCreatePath } from "../utils/processCreateRoute"
import UserHeaderMenu from "./UserHeaderMenu"
import UserPhoneEditModal from "../components/UserPhoneEditModal"
import ThemeSwitcher from "../components/ThemeSwitcher"
import { useTheme } from "../contexts/ThemeContext"
import { useBranding } from "../contexts/BrandingContext"
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from "../services/api/uiButtonStatsService"
import "./header-redesign.css"

function initialsFromName(name: string | undefined): string {
    if (!name?.trim()) return "?"
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

interface ChildProps {
    loadcompany: boolean
    listApproval?: Array<any>
    loading?: boolean,
    companyById?: iCompany
    inputValue?: string
    setSearchQuery?: (value: string | undefined | ((prev: string | undefined) => string | undefined)) => void;
    setFilter?: (value: string | undefined | ((prev: string | undefined) => string | undefined)) => void;
    search?: boolean
    searchfilter?: boolean
    filterValue?: string
    disabled?: boolean
    hideHelpAndUser?: boolean // Masquer les boutons Aide et Nom d'utilisateur (garder uniquement Langue)

}

type iState = {
    email: string,
    isSubmit: boolean,
    spinner: boolean,
    admins: iUser[],
    myCompany: iCompany[],
    // [CREDITS HEADER] État pour affichage crédits dans le header
    creditsAvailable: number | null,
    creditsLoading: boolean
}

const HeaderLayout = (props: ChildProps) => {
    const {
        loadcompany,
        listApproval,
        loading,
        companyById,
        inputValue,
        setSearchQuery,
        setFilter,
        search,
        searchfilter,
        filterValue,
        disabled = false,
        hideHelpAndUser = false
    } = props
    const users = useAppSelector((state) => state.auth);
    const logoLinksToHome = Boolean(users.access_token);
    const companyId = users.session.company_id;
    const role = useAppSelector((state) => state.role.role);
    const company = useAppSelector((state) => state.company.company);
    const location = useLocation();
    const navigate = useNavigate();
    
    // [WHITE LABELLING] Récupérer le branding personnalisé et le thème
    const { theme } = useTheme();
    const { getLogo, isCustomBranding, branding } = useBranding();
    const customLogo = getLogo(theme);
    const displayLogo = customLogo || Images.logov1;
    const searchTopbarFocusTracked = useRef(false);
    /** Même écran `Home` : route principale `/home` (DashboardView) ou `/process/overview`. */
    const trackHomeTopbar = async (key: string) => {
        const p = (location.pathname || "/").replace(/\/+$/, "") || "/";
        if (p !== Routes.HOMEVIEW && p !== Routes.PROCESS_VIEW) return;
        await trackUiButtonEventAsync(key);
    };

    const isProcessesActive = useMemo(() => {
        const p = location.pathname;
        if (p === "/" || p === Routes.HOMEVIEW) return true;
        if (p.startsWith("/process/") && !p.startsWith(Routes.PROCESS_CREATE)) return true;
        return false;
    }, [location.pathname]);

    const isCompanyAnalysisRoute = useMemo(
        () => location.pathname.startsWith(Routes.COMPANYANALYSIS),
        [location.pathname]
    );

    const handleHeaderPrimaryCreate = () => {
        void trackHomeTopbar(UI_BUTTON_EVENTS.home.topbarNewProcess);
        if (isCompanyAnalysisRoute) {
            const trigger = document.getElementById("company-analysis-new-trigger") as HTMLButtonElement | null;
            trigger?.click();
            return;
        }
        navigate(getProcessCreatePath("AI"));
    };

    // Logique conditionnelle pour le bouton Invite
    const handleInviteClick = () => {
        void trackHomeTopbar(UI_BUTTON_EVENTS.home.topbarInvite);
        const pathname = location.pathname;
        // Cas A: Pages Interview ou Analyse → ouvrir modal
        const isInterviewPage = pathname.includes(Routes.PROCESS_INTERVIEW);
        const isAnalysisPage = pathname.includes(Routes.COMPANYANALYSIS);
        
        if (isInterviewPage || isAnalysisPage) {
            // Ouvrir la modal d'invitation contextuelle
            const modalTrigger = document.getElementById('invite-modal-trigger') as HTMLButtonElement;
            if (modalTrigger) {
                modalTrigger.click();
            }
        } else {
            // Cas B: Toutes autres pages → naviguer vers page invités
            navigate(Routes.COMAPNYINVITED + '/home');
        }
    };
    const [value, setValue] = useState('')
    const defaultProjectId: string = "b15abbcd-4b91-407a-acf4-58eb7fafbdea";
    const [currentLanguage, setCurrentLanguage] = React.useState(() => {
        try {
            return localStorage.getItem('currentLanguage') || navigator.language;
        } catch (error) {
            return navigator.language;
        }
    });

    const [state, setState] = React.useState<iState>({
        email: "",
        isSubmit: false,
        spinner: true,
        admins: [],
        myCompany: [],
        creditsAvailable: null,
        creditsLoading: false
    })

    const { t } = useTranslation();

    const kbdHint =
        typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)
            ? t("layout.header.shortcutMac")
            : t("layout.header.shortcutWin");

    const [alert, setAlert] = React.useState<iAlert>({
        isActive: false,
        type: '',
        message: ''
    })

    // [DAYS REMAINING] Calcul jours restants uniquement pour plans avec expiration (PLUS expiré)
    // Pour FREE, pas d'expiration donc daysRemaing = 0
    // Note: useCountdown doit toujours être appelé (règle React hooks), mais on ignore le résultat pour FREE
    const daysRemaingRaw = useCountdown(companyById?.expired_at);
    const isPaidPlan = companyById?.status && ["ESSENTIAL", "PLUS", "ENTERPRISE"].includes(companyById.status);
    const daysRemaing = (isPaidPlan && companyById?.expired_at) ? daysRemaingRaw : 0;

    const [selectedCompany, setSelectedCompany] = useState(companyId || undefined);

    const handleSave = () => {
        if (selectedCompany && selectedCompany !== companyId) {
            switchToNewCompany(selectedCompany);
        }
    };

    const updateLang = async (lang: string) => {
        const language = lang === "fr" ? "french" : "english"
        try {
            const updateData = {
                fullname: users.user.fullname,
                phone_number: users.user.phone_number,
                lang: language,
                updated_at: users.user.created_at
            };

            let data = await FamlaServices("api_host", `/users/`, "PUT", updateData, users.access_token);
            if (data.status === 200) {
            }
        } catch (error) {

        }

    }

    React.useEffect(() => {
        changeLanguage(currentLanguage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLanguage]);

    const changeLanguage = (language: string) => {
        let updateLanguage = language === 'fr-FR' || language === 'fr' ? 'fr' : 'en';
        i18n.changeLanguage(updateLanguage);
        // let action = {
        //     type: "LANGUAGE",
        //     value: {
        //         language: updateLanguage,
        //         languageName: updateLanguage === 'fr' ? t('words.french') : t('words.english')
        //     }
        // };
        // dispatch(action);
        updateLang(updateLanguage);
        setCurrentLanguage(updateLanguage);
        localStorage.setItem('currentLanguage', updateLanguage);
    };



    const handleChange = (event: any) => {
        const { type, name, value, checked } = event.target;
        let fieldValue = type === 'checkbox' ? checked : value;

        if (type === 'number') {
            fieldValue = Number(fieldValue);
        }
        if (name === 'email') {
            setState((prev) => ({ ...prev, email: fieldValue }))
        }
    };

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();

        if (state.email.length === 0) {
            setAlert({ isActive: true, type: 'danger', message: t('navigation.enterValideEmail') });
            return;
        }

        // Vérifier si l'email est professionnel
        if (isPrivateEmail(state.email)) {
            setAlert({ isActive: true, type: 'warning', message: t('views.auth.professionalEmail') });
            return;
        }

        const formData = {
            project_id: defaultProjectId,
            mail: [state.email],
        };

        setState((prevState) => ({ ...prevState, isSubmit: true }));

        try {
            const data = await FamlaServices("api_host", `/project/add_owner/`, "PUT", formData, users.access_token);

            if (data.status === 200) {
                setAlert({ isActive: true, type: 'success', message: t('navigation.invitationSentSuccessfully') });
                setState((prevState) => ({ ...prevState, email: "" }));
                setTimeout(() => {
                    setAlert({ isActive: false, type: '', message: "" });
                }, 3000);
            } else if (data.status === 404) {
                setAlert({ isActive: true, type: 'danger', message: t('navigation.proJectNotFound') });
            } else if (data.status === 401) {
                setAlert({ isActive: true, type: 'danger', message: t("navigation.youAreNotLoggedIn") });
            } else {
                setAlert({ isActive: true, type: 'danger', message: t("navigation.anErrorOcured") });
            }
        } catch (error) {
            console.error("An error occurred:", error);
            setAlert({ isActive: true, type: 'danger', message: t("navigation.anErrorOcured") });
        } finally {
            setState((prevState) => ({ ...prevState, isSubmit: false }));
        }
    };


    React.useEffect(() => {
        async function loadAdmin() {

            setState((prevState) => ({ ...prevState, spinner: true }))
            try {
                setAlert({ isActive: false, type: '', message: '' });
                let data = await FamlaServices("api_host", `/users/company/${companyId}?page=${1}&limit=${1000}`, "GET", "", users.access_token);

                if (data.status === 200) {
                    const admin = data.body.data.admin
                    setState((prevState) => ({ ...prevState, admins: admin, spinner: false }))

                } else if (data.status === 401 && data.body.description === "You do not have authorisation to continue") {
                    setAlert({ isActive: true, type: 'warning', message: 'You do not have authorisation to continue' });
                } else if (data.status === 401) {
                    logout();
                } else {
                    setAlert({ isActive: true, type: 'danger', message: 'An error has occurred, please try again later' });
                }
            } catch (e) {
                if (e instanceof Error) {
                    console.error(e.message);
                }
            } finally {
                setState((prevState) => ({ ...prevState, spinner: false }))
            }
        }

        if (loadcompany) {
            loadAdmin();
        }

        async function loadMycompany() {

            setState((prevState) => ({ ...prevState, spinner: true }))
            try {
                setAlert({ isActive: false, type: '', message: '' });
                let data = await FamlaServices("api_host", `/company/my`, "GET", "", users.access_token);

                if (data.status === 200) {
                    const companies = data.body.data
                    setState((prevState) => ({ ...prevState, myCompany: companies, spinner: false }))

                } else if (data.status === 401 && data.body.description === "You do not have authorisation to continue") {
                    setAlert({ isActive: true, type: 'warning', message: 'You do not have authorisation to continue' });
                } else if (data.status === 401) {
                    logout();
                } else {
                    setAlert({ isActive: true, type: 'danger', message: 'An error has occurred, please try again later' });
                }
            } catch (e) {
                if (e instanceof Error) {
                    console.error(e.message);
                }
            } finally {
                setState((prevState) => ({ ...prevState, spinner: false }))
            }
        }

        loadMycompany()

        // [CHARGEMENT CRÉDITS HEADER] Charger les crédits disponibles pour affichage dans le header
        async function loadCredits() {
            if (!companyId || !users.access_token) return;
            
            setState(prevState => ({ ...prevState, creditsLoading: true }));
            try {
                const response = await CreditsService.getCreditBalance(companyId, users.access_token);
                if (response.success && response.data) {
                    setState(prevState => ({
                        ...prevState,
                        creditsAvailable: response.data.credits_available || 0,
                        creditsLoading: false
                    }));
                } else {
                    setState(prevState => ({ ...prevState, creditsLoading: false }));
                }
            } catch (error) {
                console.error('[HeaderLayout] Load credits error:', error);
                setState(prevState => ({ ...prevState, creditsLoading: false }));
            }
        }

        loadCredits();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, loadcompany, users.access_token])

    const switchToNewCompany = async (company_id: string | undefined) => {
        if (!company_id || !users.access_token) return;
        setState({ ...state, isSubmit: true });

        // /process/overview est réactive au changement de compagnie (effets indexés sur
        // companyId) : on bascule sans rechargement dur pour une transition fluide.
        // Les autres pages ne le sont pas encore et gardent le reload pour rester cohérentes.
        const path = (location.pathname || '/').replace(/\/+$/, '') || '/';
        const smoothSwitch = path === Routes.PROCESS_VIEW;

        const ok = await switchActiveCompany(
            company_id,
            users.access_token,
            smoothSwitch ? {} : { reload: true },
        );

        setState({ ...state, isSubmit: false });

        // Sans reload, il faut fermer nous-mêmes la modale Bootstrap et nettoyer le backdrop.
        if (ok && smoothSwitch) {
            const node = document.getElementById('changeCompany');
            try {
                (window as any).bootstrap?.Modal?.getInstance(node)?.hide();
            } catch {
                /* ignore */
            }
            document.querySelectorAll('.modal-backdrop').forEach((b) => b.remove());
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
        }
    }

    function setFilters(value: string) {
        if (value) {
            setFilter?.(value)
            setValue(value)
        }

    }

    const planLabel = planLabelFromCompany(t, {
        plan: companyById?.plan || company?.plan,
        status: companyById?.status || company?.status,
    });

    // Raccourci Ctrl/Cmd+K désactivé (recherche globale) — demande QA
    // React.useEffect(() => {
    //     if (!search) return;
    //     const onKey = (e: KeyboardEvent) => {
    //         if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
    //             e.preventDefault();
    //             const el = document.querySelector(".hdr-search-box input") as HTMLInputElement | null;
    //             el?.focus();
    //         }
    //     };
    //     window.addEventListener("keydown", onKey);
    //     return () => window.removeEventListener("keydown", onKey);
    // }, [search]);

    return (
        <>
            <header>
                <div
                    style={{ pointerEvents: disabled ? "none" : "auto" }}
                    className={`hdr-top navbar navbar-expand-lg ${theme === "light" ? "navbar-light" : "navbar-dark"}`}
                >
                    {logoLinksToHome ? (
                        <NavLink
                            className="hdr-logo-link"
                            to={Routes.HOMEVIEW}
                            title={isCustomBranding ? branding.appName : t("layout.header.brandAlt")}
                            onClick={() => void trackHomeTopbar(UI_BUTTON_EVENTS.home.topbarLogo)}
                        >
                            <img
                                src={displayLogo}
                                alt={isCustomBranding ? branding.appName : t("layout.header.brandAlt")}
                                className="hdr-logo-img"
                            />
                        </NavLink>
                    ) : (
                        <div className="hdr-logo-static">
                            <img
                                src={displayLogo}
                                alt={isCustomBranding ? branding.appName : t("layout.header.brandAlt")}
                                className="hdr-logo-img"
                            />
                        </div>
                    )}

                    <button
                        className="navbar-toggler hdr-mobile-toggle d-lg-none"
                        type="button"
                        aria-label={t("layout.header.menuToggle")}
                        onClick={() => void trackHomeTopbar(UI_BUTTON_EVENTS.home.topbarMobileNav)}
                    >
                        <span className="navbar-toggler-icon" />
                    </button>

                    <div className="navbar-collapse d-flex flex-grow-1 min-w-0" id="hdrNavbarContent">
                        <div className="hdr-nav-inner w-100">
                            {search && (
                                <div className="hdr-search-box">
                                    <div className="hdr-search-wrap">
                                        <SearchInput
                                            inputType="text"
                                            inputValue={inputValue}
                                            placeholderValue={`${t("words.search")}...`}
                                            onFocus={() => {
                                                if (searchTopbarFocusTracked.current) return;
                                                searchTopbarFocusTracked.current = true;
                                                void trackHomeTopbar(
                                                    UI_BUTTON_EVENTS.home.topbarSearchFocus
                                                );
                                            }}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                setSearchQuery?.(event.target.value)
                                            }
                                            inputContainerClass="w-100"
                                            inputStyle={{}}
                                            inputClass="new-text-secondary border-0 small"
                                            iconClass="fa-solid fa-magnifying-glass"
                                            iconName=""
                                            formClass="border-0 bg-transparent small w-100 shadow-none hdr-search-form"
                                            iconContainer="border-0 bg-transparent"
                                            setFilter={setFilter}
                                            filter={true}
                                            fiterOption={[]}
                                        />
                                    </div>
                                    {/* <span className="hdr-kbd d-none d-sm-inline">{kbdHint}</span> */}
                                </div>
                            )}

                            {/* {searchfilter && (
                                <div className="dropdown hdr-filter-dd">
                                    <button
                                        className="dropdown-toggle"
                                        type="button"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        {value === ""
                                            ? t("words.filter")
                                            : value === "TITLE"
                                              ? `${t("words.name")}/${t("words.description")}`
                                              : t("words.role")}
                                    </button>
                                    <ul className="dropdown-menu hdr-filter-popup border">
                                        <li onClick={() => setFilters?.("ROLE")}>
                                            <button type="button" className="dropdown-item hdr-filter-popup-item text-decoration-none border-0 bg-transparent w-100 text-start">
                                                {t("words.role")}
                                            </button>
                                        </li>
                                        <li onClick={() => setFilters?.("TITLE")}>
                                            <button type="button" className="dropdown-item hdr-filter-popup-item text-decoration-none border-0 bg-transparent w-100 text-start">
                                                {`${t("words.name")}/${t("words.description")}`}
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            )} */}

                            <div className="hdr-shortcuts">
                                <NavLink
                                    to={Routes.PROCESSLIBRARY}
                                    className={() =>
                                        `hdr-shortcut ${isProcessesActive ? "hdr-shortcut--active" : ""}`
                                    }
                                    end
                                    onClick={() =>
                                        void trackHomeTopbar(
                                            UI_BUTTON_EVENTS.home.topbarProcessesShortcut
                                        )
                                    }
                                >
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
                                        <path
                                            d="M2 4.5C2 3.4 2.9 2.5 4 2.5H12C13.1 2.5 14 3.4 14 4.5V5.5C14 6.6 13.1 7.5 12 7.5H4C2.9 7.5 2 6.6 2 5.5V4.5Z"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                        />
                                        <path
                                            d="M2 10.5C2 9.4 2.9 8.5 4 8.5H8C9.1 8.5 10 9.4 10 10.5V11.5C10 12.6 9.1 13.5 8 13.5H4C2.9 13.5 2 12.6 2 11.5V10.5Z"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                        />
                                    </svg>
                                    {t("form.createProcess.tabProcesses")}
                                </NavLink>
                                <button
                                    type="button"
                                    className="hdr-shortcut"
                                    onClick={handleInviteClick}
                                >
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
                                        <circle cx="6" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.2" />
                                        <path
                                            d="M1 13C1 10.8 3.2 9 6 9C8.8 9 11 10.8 11 13"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            d="M11.5 7V11M9.5 9H13.5"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    {t("words.invite")}
                                </button>
                                {/* Hidden trigger button for modal (used programmatically) */}
                                <button
                                    type="button"
                                    className="d-none"
                                    data-bs-toggle="modal"
                                    data-bs-target="#inviteOwner"
                                    id="invite-modal-trigger"
                                />
                            </div>

                            <div className="hdr-spacer" aria-hidden />

                            <div className="hdr-right">
                                {state.creditsAvailable !== null && hideHelpAndUser && (
                                    <>
                                        {role === "ADMIN" ? (
                                            <NavLink
                                                to={Routes.DASHBOARDS}
                                                className="hdr-credits"
                                                title={t("dashboard.availableCredits", "Available Credits")}
                                            >
                                                <i className="fa-solid fa-coins" style={{ color: "#ffc107" }} />
                                                <span className={state.creditsAvailable > 0 ? "text-success" : "text-danger"}>
                                                    {formatNumber(state.creditsAvailable)}
                                                </span>
                                            </NavLink>
                                        ) : (
                                            <div className="hdr-credits" title={t("dashboard.availableCredits", "Available Credits")}>
                                                <i className="fa-solid fa-coins" style={{ color: "#ffc107" }} />
                                                <span className={state.creditsAvailable > 0 ? "text-success" : "text-danger"}>
                                                    {formatNumber(state.creditsAvailable)}
                                                </span>
                                            </div>
                                        )}
                                    </>
                                )}

                                <ThemeSwitcher
                                    onToggle={() => void trackHomeTopbar(UI_BUTTON_EVENTS.home.topbarTheme)}
                                />

                                <Dropdown
                                    onMenuButtonClick={() =>
                                        void trackHomeTopbar(UI_BUTTON_EVENTS.home.topbarLangOpen)
                                    }
                                    key={3}
                                    dropName={
                                        <>
                                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
                                                <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.2" />
                                                <path
                                                    d="M8 2.5C8 2.5 6 5 6 8C6 11 8 13.5 8 13.5"
                                                    stroke="currentColor"
                                                    strokeWidth="1.2"
                                                />
                                                <path
                                                    d="M8 2.5C8 2.5 10 5 10 8C10 11 8 13.5 8 13.5"
                                                    stroke="currentColor"
                                                    strokeWidth="1.2"
                                                />
                                                <path d="M2.5 8H13.5" stroke="currentColor" strokeWidth="1.2" />
                                            </svg>
                                            {currentLanguage === "fr"
                                                ? t("layout.header.langShortFR")
                                                : t("layout.header.langShortEN")}
                                        </>
                                    }
                                    dropdownType="dropdown"
                                    buttonDropClassName="dropdown-toggle hdr-dd-btn"
                                    styleButton={{}}
                                    theRouteOfAction4=""
                                    theRouteOfAction5=""
                                    lang={true}
                                    changeLanguage={changeLanguage}
                                />

                                {!hideHelpAndUser && (
                                    <>
                                        <Dropdown
                                            onMenuButtonClick={() =>
                                                void trackHomeTopbar(
                                                    UI_BUTTON_EVENTS.home.topbarHelpOpen
                                                )
                                            }
                                            key={2}
                                            dropName={
                                                <>
                                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
                                                        <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.2" />
                                                        <path
                                                            d="M6.5 6.5C6.5 5.7 7.2 5 8 5C8.8 5 9.5 5.7 9.5 6.5C9.5 7.3 8 8 8 9"
                                                            stroke="currentColor"
                                                            strokeWidth="1.2"
                                                            strokeLinecap="round"
                                                        />
                                                        <circle cx="8" cy="11" r=".8" fill="currentColor" />
                                                    </svg>
                                                    {t("words.help")}
                                                </>
                                            }
                                            dropdownType="dropstart"
                                            buttonDropClassName="dropdown-toggle hdr-dd-btn hdr-dd-help"
                                            styleButton={{}}
                                            deleteProcess={() => {}}
                                            action4={t("navigation.contactUs")}
                                            action5={t("navigation.privacyHub")}
                                            theRouteOfAction4={Routes.CONTACTUS}
                                            theRouteOfAction5={Routes.PRIVACY}
                                            faq={t("words.faq")}
                                            routeFaq={Routes.FAQ}
                                            editdiadram={t("navigation.userGuide")}
                                            runEditor={openPDFInViewer}
                                        />

                                        <button
                                            type="button"
                                            className="hdr-btn-new"
                                            onClick={handleHeaderPrimaryCreate}
                                        >
                                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
                                                <path
                                                    d="M8 2V14M2 8H14"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                            {isCompanyAnalysisRoute
                                                ? t("features.swimlane.newAnalysis")
                                                : t("views.processView.newProcess")}
                                        </button>

                                        {/* Masqué sur la page Analyse : le profil est déjà dans la sidebar gauche */}
                                        {!isCompanyAnalysisRoute && (
                                            <UserHeaderMenu
                                                t={t}
                                                loadcompany={loadcompany}
                                                fullname={users.user.fullname}
                                                email={users.user.email}
                                                role={role}
                                                planLabel={planLabel}
                                                creditsAvailable={state.creditsAvailable}
                                                creditsLoading={state.creditsLoading}
                                                isAdmin={role === "ADMIN"}
                                                trackHomeTopbar={trackHomeTopbar}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </header>
            

            <InviteOwnerModal
                alert={alert}
                email={state.email}
                isSubmitting={state.isSubmit}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                t={t}
            />


            <ChangeCompanyModal
                companies={state.myCompany}
                currentCompanyId={companyId}
                onSave={switchToNewCompany}
                t={t}
            />

        <ApprovalRequestModal
            approvalList={listApproval}
            loading={loading}
        />

            <UserPhoneEditModal
                accessToken={users.access_token}
                initialPhone={users.user.phone_number}
            />

        </>
    )
}

export default HeaderLayout 